import React from "react";

// Ticket #224 fase 3 — subcomponente memoizado de la CAJA 5 del HUD
// (tabs Inventario / Hechizos + grids + action row + PARTY button).
//
// A diferencia de StatsBox (fase 1) y MapBox (fase 2), este NO mueve la
// lógica de renderizado afuera. La razón es que renderBoxItems,
// renderBoxSpells y renderSpellActionRow del padre tienen tentáculos por
// todos lados: 10+ event handlers (drag/drop, click, right-click, tooltip),
// múltiples lecturas de state, helpers (keyCodeToChar, getEffectiveSpellOrder),
// globals (inits.objs, inits.graphics, buildItemTooltip). Mover todo eso
// sería un refactor masivo con alto riesgo de regresiones.
//
// En su lugar: el padre PASA esas 3 funciones como callback props. Acá
// las invocamos. `shouldComponentUpdate` compara primitivos para decidir
// si dejar pasar el render — cuando HP/Mana cambia, el inventario no se
// retoca aunque el padre re-renderee.
//
// Hashes (itemsHash, spellsHash) capturan el estado relevante del dict
// (slot:idItem:cant:equipped → string). El padre los calcula una vez por
// render y los pasa como primitivos. Cualquier cambio real en items o
// hechizos modifica el hash → re-render.
// Ticket #261: extends Component (NO PureComponent) — `shouldComponentUpdate`
// custom es incompatible con PureComponent (warning de React). El SCU custom
// hace el shallow-compare que necesitamos sobre props específicos (hash-based).
export default class InventoryBox extends React.Component {
    shouldComponentUpdate(nextProps) {
        const p = this.props;
        // Ticket #336 v2: PartyPanel se memoizó en el parent vía
        // _getCachedPartyPanelEl. El parent reusa la misma referencia
        // del element JSX cuando el state del party no cambió → acá la
        // comparación ref `partyPanelEl !== nextProps.partyPanelEl`
        // hace bail correctamente. NO bypaseamos por showPartyPanel.
        return (
            p.showInventary !== nextProps.showInventary ||
            p.itemsHash !== nextProps.itemsHash ||
            p.spellsHash !== nextProps.spellsHash ||
            p.selectItem !== nextProps.selectItem ||
            p.selectedSpellSlot !== nextProps.selectedSpellSlot ||
            p.invDraggingFrom !== nextProps.invDraggingFrom ||
            p.invDraggingOver !== nextProps.invDraggingOver ||
            p.sortMenuOpen !== nextProps.sortMenuOpen ||
            p.partyMembersCount !== nextProps.partyMembersCount ||
            p.inventoryKeybindingsRef !== nextProps.inventoryKeybindingsRef ||
            p.spellKeybindingsRef !== nextProps.spellKeybindingsRef ||
            p.spellOrderRef !== nextProps.spellOrderRef ||
            p.hasClan !== nextProps.hasClan ||
            p.clanMemberCount !== nextProps.clanMemberCount ||
            p.clanMemberCap !== nextProps.clanMemberCap ||
            p.showPartyPanel !== nextProps.showPartyPanel ||
            p.partyPanelEl !== nextProps.partyPanelEl ||
            p.showGlobalPanel !== nextProps.showGlobalPanel ||
            p.globalPanelEl !== nextProps.globalPanelEl ||
            p.globalListCount !== nextProps.globalListCount ||
            p.showClanPanel !== nextProps.showClanPanel ||
            p.clanPanelEl !== nextProps.clanPanelEl
        );
    }

    render() {
        const {
            style,
            showInventary,
            onShowInv,
            onShowSpells,
            onShowPartyModal,
            onShowGlobalPanel,
            onShowClanModal,
            renderBoxItems,
            renderBoxSpells,
            renderSpellActionRow,
            partyPanelEl,
            showPartyPanel,
            partyMembersCount,
            globalPanelEl,
            showGlobalPanel,
            globalListCount,
            clanPanelEl,
            showClanPanel,
            hasClan,
            clanMemberCount,
            clanMemberCap
        } = this.props;

        return (
            <div className={style.body}>
                <div className={style.bodyTabs}>
                    <button
                        type="button"
                        className={`${style.bodyTab} ${
                            showInventary ? style.bodyTabActive : ""
                        }`}
                        onClick={onShowInv}
                    >
                        Inventario
                    </button>
                    <button
                        type="button"
                        className={`${style.bodyTab} ${
                            !showInventary ? style.bodyTabActive : ""
                        }`}
                        onClick={onShowSpells}
                    >
                        Hechizos
                    </button>
                </div>
                <div className={style.bodyPanel}>
                    {showInventary ? (
                        <>
                            <div className={style.itemsHeader}>
                                <span className={style.itemsTitle}>Items</span>
                                <span className={style.itemsRule} />
                            </div>
                            <div className={style.inventary}>
                                {renderBoxItems()}
                            </div>
                        </>
                    ) : (
                        <div className={style.spell}>{renderBoxSpells()}</div>
                    )}
                </div>
                {!showInventary && renderSpellActionRow()}
                {/* Ticket #336 v2: panel inline del Party. El element ya
                    viene memoizado del parent (_getCachedPartyPanelEl) —
                    en cache hit es la misma ref, en miss es un PartyPanel
                    React.memo'd que se renderea solo si cambian sus props. */}
                {showInventary && showPartyPanel && partyPanelEl}
                {/* Panel GLOBAL — lista de chars PvE conectados.
                    Mismo patrón de memoization que el Party. */}
                {showInventary && showGlobalPanel && globalPanelEl}
                {/* Panel CLAN — mismo patrón, mutual exclusion con los otros. */}
                {showInventary && showClanPanel && clanPanelEl}
                {showInventary && (
                    <div className={style.partyButtonRow}>
                        <button
                            type="button"
                            className={`${style.partyButton} ${
                                showPartyPanel ? style.partyButtonActive : ""
                            }`}
                            onClick={onShowPartyModal}
                        >
                            <span
                                className={style.partyButtonIcon}
                                aria-hidden="true"
                            >👤</span>
                            PARTY
                        </button>
                        <button
                            type="button"
                            className={`${style.partyButton} ${
                                showClanPanel ? style.partyButtonActive : ""
                            }`}
                            onClick={onShowClanModal}
                        >
                            <span
                                className={style.partyButtonIcon}
                                aria-hidden="true"
                            >⚔️</span>
                            CLAN
                        </button>
                        <button
                            type="button"
                            className={`${style.partyButton} ${
                                showGlobalPanel ? style.partyButtonActive : ""
                            }`}
                            onClick={onShowGlobalPanel}
                        >
                            <span
                                className={style.partyButtonIcon}
                                aria-hidden="true"
                            >🌐</span>
                            GLOBAL
                        </button>
                    </div>
                )}
            </div>
        );
    }
}
